import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetRecipeDraft,
  getGetRecipeDraftQueryKey,
  useSaveRecipeDraft,
  useApproveRecipeDrafts,
  useRedraftCostRecipe,
  useSkipRecipeDraft,
  type RecipeDraftRow,
  type RecipeDraftApprovalResult,
  type CostRecipe as ApiCostRecipe,
} from "@workspace/api-client-react";
import type { CostRecipe } from "@workspace/api-zod/cost-recipe";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, Loader2, RefreshCw, SkipForward, AlertTriangle } from "lucide-react";
import { CostRecipeEditor } from "./cost-recipe-editor";
import { errorMessage } from "./cost-recipe-panel";
import {
  STATUS_LABELS, approvalBlocker, asApprovalResult, formatGBP, formatSignedGBP,
  invalidateAfterPublish, invalidateDrafts, isBalanced,
} from "@/lib/recipe-drafts";

interface Props {
  row: RecipeDraftRow | null;
  onClose: () => void;
}

export function RecipeDraftReviewDialog({ row, onClose }: Props) {
  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1.5rem)] max-h-[92dvh] overflow-y-auto p-4 sm:p-6" data-testid="dialog-recipe-draft">
        {row && <DraftReviewBody key={row.id} taskId={row.id} fallback={row} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function DraftReviewBody({ taskId, fallback, onClose }: { taskId: string; fallback: RecipeDraftRow; onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [mode, setMode] = useState<"view" | "edit" | "redraft">("view");
  const [note, setNote] = useState("");
  const [result, setResult] = useState<RecipeDraftApprovalResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Refetch on open only; deliberately no interval so polling never clobbers edits.
  const detailQ = useGetRecipeDraft(taskId, {
    query: { queryKey: getGetRecipeDraftQueryKey(taskId), staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: false },
  });
  const detail = detailQ.data;

  const onDone = () => { void invalidateDrafts(qc); };

  const approve = useApproveRecipeDrafts({
    mutation: {
      onSuccess: (r) => {
        setResult(r);
        void invalidateAfterPublish(qc);
        if (r.approved > 0) {
          toast({ title: "Recipe approved and published", description: "Typical price unchanged. It now shows in the Cost Library breakdown panel." });
          onClose();
        }
      },
      onError: (err) => {
        const r = asApprovalResult(err);
        if (r) setResult(r);
        setActionError(r ? "Approval was skipped by the server." : errorMessage(err));
        void invalidateDrafts(qc);
      },
    },
  });
  const save = useSaveRecipeDraft({
    mutation: {
      onSuccess: () => {
        toast({ title: "Draft saved", description: "Kept in staging. Nothing is published until you approve." });
        setMode("view");
        onDone();
      },
      onError: (err) => setActionError(errorMessage(err)),
    },
  });
  const redraft = useRedraftCostRecipe({
    mutation: {
      onSuccess: () => {
        toast({ title: "Redraft queued", description: note.trim() ? "Your note goes into the next AI call." : "The task will be drafted again." });
        onDone();
        onClose();
      },
      onError: (err) => setActionError(errorMessage(err)),
    },
  });
  const skip = useSkipRecipeDraft({
    mutation: {
      onSuccess: () => {
        toast({ title: "Skipped for now", description: "Nothing was published or deleted. You can redraft it later." });
        onDone();
        onClose();
      },
      onError: (err) => setActionError(errorMessage(err)),
    },
  });

  const busy = approve.isPending || save.isPending || redraft.isPending || skip.isPending;
  const r = detail ?? fallback;
  const blocker = detail ? approvalBlocker(detail) : null;
  const recipe = detail?.draftRecipe ?? null;

  function doApprove(edited?: CostRecipe) {
    if (!detail) return;
    setActionError(null); setResult(null);
    approve.mutate({ data: { taskIds: [detail.id], expectedRevision: detail.revision, ...(edited ? { editedRecipe: edited } : {}) } });
  }

  return (
    <>
      <DialogHeader className="text-left space-y-1.5">
        <DialogTitle className="pr-6 leading-snug" data-testid="text-draft-name">{r.name}</DialogTitle>
        <DialogDescription asChild>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span>{r.category}</span>
            <span>per {r.unit}</span>
            <span className="tabular-nums">Current typical <strong className="text-foreground">{formatGBP(r.currentTypical)}</strong></span>
            <Badge variant="secondary" className="font-normal" data-testid="badge-draft-status">{STATUS_LABELS[r.status]}</Badge>
            {r.usageRank !== null && <span className="tabular-nums">Dev usage rank #{r.usageRank}</span>}
            <span className="tabular-nums">Revision {r.revision}</span>
          </div>
        </DialogDescription>
      </DialogHeader>

      <p className="text-xs rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 px-3 py-2">
        Material prices and quantities are AI estimates from model knowledge, not live merchant quotes. Check them before approving. Approval never changes the typical, low, high or lock.
      </p>

      {detailQ.isLoading ? (
        <div className="space-y-2" data-testid="skeleton-draft-detail">
          <Skeleton className="h-5 w-48" /><Skeleton className="h-24 w-full" /><Skeleton className="h-24 w-full" />
        </div>
      ) : detailQ.isError || !detail ? (
        <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="error-draft-detail">
          <span className="text-destructive">Could not load this draft. {errorMessage(detailQ.error)}</span>
          <Button size="sm" variant="outline" onClick={() => detailQ.refetch()} data-testid="button-retry-draft-detail">Retry</Button>
        </div>
      ) : (
        <div className="space-y-4 min-w-0">
          {(detail.description || detail.notesIncluded || detail.notesExcluded) && (
            <div className="text-xs space-y-1 rounded-md border p-3" data-testid="text-draft-scope">
              {detail.description && <p>{detail.description}</p>}
              {detail.notesIncluded && <p><strong>Included:</strong> {detail.notesIncluded}</p>}
              {detail.notesExcluded && <p><strong>Excluded:</strong> {detail.notesExcluded}</p>}
            </div>
          )}
          {!!detail.reviewWarnings?.length && (
            <div className="text-xs rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950" data-testid="text-review-warnings">
              <p className="font-semibold">Scope checks to review before approval</p>
              <ul className="list-disc pl-4">{detail.reviewWarnings.map(warning => <li key={warning}>{warning}</li>)}</ul>
              <p className="mt-1">These are review hints, not verified findings. Edit or redraft if the scope is wrong.</p>
            </div>
          )}
          {(detail.note || detail.ownerNote) && (
            <div className="text-xs space-y-1 rounded-md bg-muted/60 p-3">
              {detail.note && <p data-testid="text-draft-note"><span className="font-semibold">Drafting note:</span> {detail.note}</p>}
              {detail.ownerNote && <p data-testid="text-owner-note"><span className="font-semibold">Your last redraft note:</span> {detail.ownerNote}</p>}
            </div>
          )}
          {detail.isStale && (
            <p className="text-xs text-destructive flex gap-1.5" data-testid="text-draft-stale">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" /> The task's price or unit changed since this draft was made. Redraft it before approval.
            </p>
          )}

          {mode === "redraft" ? (
            <div className="space-y-2">
              <Label htmlFor="redraft-note" className="text-xs">Note for the next AI call (optional)</Label>
              <Textarea id="redraft-note" rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Use 100mm blocks, not 140mm. Plasterboard price looks high." data-testid="input-redraft-note" />
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button variant="ghost" onClick={() => setMode("view")} disabled={busy} data-testid="button-cancel-redraft">Cancel</Button>
                <Button onClick={() => { setActionError(null); redraft.mutate({ id: detail.id, data: note.trim() ? { note: note.trim() } : {} }); }}
                  disabled={busy} data-testid="button-confirm-redraft">
                  {redraft.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Queue redraft
                </Button>
              </div>
            </div>
          ) : mode === "edit" && recipe ? (
            <CostRecipeEditor
              key={`${detail.id}-${detail.revision}`}
              taskId={detail.id}
              unitLabel={detail.unit}
              initial={recipe}
              currentTypical={detail.currentTypical}
              formatCurrency={formatGBP}
              isSaving={approve.isPending || save.isPending}
              serverError={null}
              isFirstAdd={false}
              recipeStatus="reviewed"
              showBalance
              requireExactBalance
              saveLabel="Approve edited recipe"
              secondaryAction={{
                label: "Save draft",
                testId: "button-save-draft",
                onClick: (rec) => { setActionError(null); save.mutate({ id: detail.id, data: { recipe: rec, revision: detail.revision } }); },
              }}
              onCancel={() => setMode("view")}
              onSave={(rec) => doApprove(rec)}
            />
          ) : recipe ? (
            <DraftSummary detailId={detail.id} recipe={recipe} unit={detail.unit} derived={detail.derivedTypical} diff={detail.sumDifference} invalid={detail.invalid} />
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="text-no-draft">
              {detail.generationState === "queued" || detail.generationState === "running"
                ? "Drafting in progress. This view refreshes when you reopen it."
                : detail.hasLiveRecipe ? "This task already has a live reviewed recipe." : "No draft yet. Queue a redraft to draft this task alone."}
            </p>
          )}

          {actionError && <p className="text-sm text-destructive" data-testid="error-draft-action">{actionError}</p>}
          {result && result.details.length > 0 && (
            <ul className="text-xs text-destructive list-disc pl-5" data-testid="list-approval-skips">
              {result.details.map((d) => <li key={d.id}>{d.reason}</li>)}
            </ul>
          )}

          {mode === "view" && (
            <div className="flex flex-wrap gap-2 border-t pt-3">
              {recipe && (
                <>
                  <Button size="sm" onClick={() => doApprove()} disabled={busy || !!blocker} title={blocker ?? undefined} data-testid="button-approve-draft">
                    {approve.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setActionError(null); setMode("edit"); }}
                    disabled={busy || detail.status === "approved" || detail.hasLiveRecipe} data-testid="button-edit-approve-draft">
                    Edit &amp; approve
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" onClick={() => { setActionError(null); setMode("redraft"); }}
                disabled={busy || detail.hasLiveRecipe || detail.generationState === "queued" || detail.generationState === "running"} data-testid="button-redraft-draft">
                <RefreshCw className="h-4 w-4 mr-1" /> Redraft
              </Button>
              {detail.draftId && detail.status !== "approved" && detail.status !== "rejected" && (
                <Button size="sm" variant="ghost" onClick={() => { setActionError(null); skip.mutate({ id: detail.id, data: { revision: detail.revision } }); }}
                  disabled={busy} data-testid="button-skip-draft">
                  {skip.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <SkipForward className="h-4 w-4 mr-1" />} Skip for now
                </Button>
              )}
              {blocker && recipe && <span className="text-xs text-muted-foreground self-center" data-testid="text-approve-blocker">{blocker}</span>}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function DraftSummary({ detailId, recipe, unit, derived, diff, invalid }: {
  detailId: string; recipe: ApiCostRecipe;
  unit: string; derived: string | null; diff: string | null; invalid: boolean;
}) {
  const ok = isBalanced({ sumDifference: diff, invalid });
  return (
    <div className="space-y-3 min-w-0" data-testid={`summary-draft-${detailId}`}>
      <div className={`rounded-md p-3 flex flex-wrap items-baseline justify-between gap-2 ${ok ? "bg-emerald-50 dark:bg-emerald-950/30" : "bg-destructive/10"}`}>
        <span className="text-sm">Derived typical per {unit}</span>
        <span className="text-lg font-semibold tabular-nums" data-testid="text-draft-derived">{derived !== null ? formatGBP(derived) : "—"}</span>
        <span className={`w-full text-xs font-medium tabular-nums ${ok ? "text-emerald-700 dark:text-emerald-400" : "text-destructive"}`} data-testid="text-draft-balance">
          {invalid ? "Invalid draft - does not match the recipe schema. Edit or redraft." : ok ? "Matches current typical to the penny." : `Signed difference ${formatSignedGBP(diff)}. Cannot be approved as-is.`}
        </span>
      </div>
      <dl className="grid gap-1 text-sm">
        <div className="flex justify-between gap-2 sm:justify-start"><dt className="text-muted-foreground sm:w-44">Shape</dt><dd className="capitalize">{recipe.shape}</dd></div>
        <div className="flex justify-between gap-2 sm:justify-start"><dt className="text-muted-foreground sm:w-44">Labour allowance</dt><dd className="tabular-nums">{formatGBP(recipe.labour_allowance)}</dd></div>
        {recipe.plant_allowance > 0 && <div className="flex justify-between gap-2 sm:justify-start"><dt className="text-muted-foreground sm:w-44">Plant allowance</dt><dd className="tabular-nums">{formatGBP(recipe.plant_allowance)}</dd></div>}
        {recipe.shape === "specification" && (
          <>
            <div className="flex justify-between gap-2 sm:justify-start"><dt className="text-muted-foreground sm:w-44">Specification</dt><dd className="break-words min-w-0 text-right sm:text-left">{recipe.specification}</dd></div>
            <div className="flex justify-between gap-2 sm:justify-start"><dt className="text-muted-foreground sm:w-44">Supply allowance</dt><dd className="tabular-nums">{formatGBP(recipe.supply_allowance ?? 0)}</dd></div>
          </>
        )}
      </dl>
      {recipe.materials.length > 0 ? (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="bg-muted/60 text-muted-foreground">
              <tr><th className="text-left p-2 font-medium">Material (estimate)</th><th className="text-right p-2 font-medium">Qty / {unit}</th><th className="text-right p-2 font-medium">Waste</th><th className="text-right p-2 font-medium">Unit price</th></tr>
            </thead>
            <tbody>
              {recipe.materials.map((m) => (
                <tr key={m.material_id} className="border-t align-top" data-testid={`row-draft-material-${m.material_id}`}>
                  <td className="p-2 min-w-[180px]"><div className="font-medium">{m.name}{m.spec ? <span className="text-muted-foreground font-normal"> ({m.spec})</span> : null}</div><div className="text-muted-foreground">{m.assumption}</div></td>
                  <td className="p-2 text-right tabular-nums whitespace-nowrap">{m.base_factor} {m.unit}</td>
                  <td className="p-2 text-right tabular-nums">{m.waste_pct}%</td>
                  <td className="p-2 text-right tabular-nums">{formatGBP(m.unit_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-xs text-muted-foreground">No materials - labour-only item.</p>}
    </div>
  );
}
